import React, { useCallback, useEffect, useState } from "react";

import { get } from "lodash";
import { partition } from "lodash/collection";

import { bulkCreateSellPossessions, bulkCreateTakePossessions } from "actions";

import { Column } from "constants/columns";
import { NLISFileTypes } from "constants/nlis";

import { openNLISTransferReceipt } from "lib/navigation";

import { ClickableColumn } from "./columnStyles";

const fileTypeFieldMap = {
  [NLISFileTypes.NLISTakeFile]: "scan.latest_take_file_id",
  [NLISFileTypes.NLISSellFile]: "scan.latest_sell_file_id",
};

export const TransferType = {
  TAKE: "TAKE",
  SELL: "SELL",
};

function getIsTransferable(props) {
  const { api, node, transferType } = props;

  if (transferType === TransferType.TAKE) {
    return api.getCellValue({ colKey: Column.IS_TAKEABLE, rowNode: node });
  } else if (transferType === TransferType.SELL) {
    return api.getCellValue({ colKey: Column.IS_SELLABLE, rowNode: node });
  }
  return false;
}

export function NLISFileRenderer({ data, nlisFileType, context, value }) {
  const nlisFileIdField = fileTypeFieldMap[nlisFileType];
  const nlisFileId = get(data, nlisFileIdField);
  const openModal = useCallback(() => {
    openNLISTransferReceipt(nlisFileId, nlisFileType);
  }, [nlisFileId, nlisFileType]);

  if (!data) {
    return null;
  }

  if (!nlisFileId) {
    return "-";
  }

  if (value) {
    return <ClickableColumn onClick={openModal}>{value}</ClickableColumn>;
  }

  return (
    <ClickableColumn onClick={context.handleNLISRefresh}>
      Refresh
    </ClickableColumn>
  );
}

export function NLISRelatedTransaction({ data, context, value }) {
  if (!data || value === null) {
    return null;
  }

  if (value) {
    return (
      <ClickableColumn
        onClick={() => openNLISTransferReceipt(value.id, value.transferType)}
      >
        {value.transactionId}
      </ClickableColumn>
    );
  }

  return (
    <ClickableColumn onClick={context.handleNLISRefresh}>
      Refresh
    </ClickableColumn>
  );
}

export function NlisStatusGroupCellRenderer(props) {
  const { api, column, context, node, transferType, value } = props;

  const [isEnabled, setIsEnabled] = useState(false);

  const invokeRerender = useCallback(() => {
    api.refreshCells({
      columns: [column.colId],
      force: true,
    });
  }, [api, column]);

  useEffect(() => {
    api.addEventListener("rowDataChanged", invokeRerender);
    return () => {
      api.removeEventListener("rowDataChanged", invokeRerender);
    };
  }, [api, invokeRerender]);

  const { childrenAfterFilter } = node;
  const leafChildren = [];
  function pushLeafNodes(rowNode) {
    if (rowNode.childrenAfterFilter) {
      rowNode.childrenAfterFilter.forEach(pushLeafNodes);
    } else {
      leafChildren.push(rowNode);
    }
  }

  childrenAfterFilter.forEach(pushLeafNodes);

  const childrenCount = leafChildren.length;

  const [transferableNodes] = partition(leafChildren, rowNode =>
    getIsTransferable({ node: rowNode, api, transferType }),
  );

  function onClickTransfer() {
    setIsEnabled(false);
    const transferableEids = transferableNodes.map(rowNode =>
      api.getCellValue({ colKey: Column.EID, rowNode }),
    );
    if (transferType === TransferType.TAKE) {
      context.dispatch(bulkCreateTakePossessions(transferableEids));
    } else if (transferType === TransferType.SELL) {
      context.dispatch(bulkCreateSellPossessions(transferableEids));
    }
  }

  const transferableEidCount = transferableNodes.length;

  useEffect(() => {
    if (transferableEidCount) {
      setIsEnabled(true);
    }
  }, [transferableEidCount]);

  return (
    <span>
      {value}{" "}
      {transferableEidCount > 0 && (
        <input
          type="button"
          onClick={onClickTransfer}
          disabled={!isEnabled}
          value={`Transfer ${transferableEidCount}${
            transferableEidCount !== childrenCount ? ` of ${childrenCount}` : ""
          }`}
        />
      )}
    </span>
  );
}

export function NlisStatusCellRenderer(props) {
  const { api, context, node, transferType, value } = props;

  const [isEnabled, setIsEnabled] = useState(false);

  const eid = api.getCellValue({ colKey: Column.EID, rowNode: node });

  function onClickTransfer() {
    setIsEnabled(false);
    if (transferType === TransferType.TAKE) {
      context.dispatch(bulkCreateTakePossessions([eid]));
    } else if (transferType === TransferType.SELL) {
      context.dispatch(bulkCreateSellPossessions([eid]));
    }
  }

  const isTransferable = getIsTransferable(props);

  useEffect(() => {
    if (isTransferable) {
      setIsEnabled(true);
    }
  }, [isTransferable]);

  if (isTransferable) {
    return (
      <span>
        {value}{" "}
        <input
          type="button"
          onClick={onClickTransfer}
          value="Transfer"
          disabled={!isEnabled}
        />
      </span>
    );
  }
  return <span>{value}</span>;
}

export function NlisStatusRenderer(props) {
  if (props.node.group) {
    return <NlisStatusGroupCellRenderer {...props} />;
  }
  return <NlisStatusCellRenderer {...props} />;
}
